import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  getOptionsArray,
  hasClaimType,
  hasClaimTypeAndValue,
  isContainSameClaimOptionValues,
} from "data/functions";
import {
  Claim,
  ServiceClaim,
  ServiceSummary,
  User,
  UserWithClaims,
} from "data/interfaces";
import { useState } from "react";

interface AssignUserRowProps {
  showDropdown: boolean;
  user: User;
  selectedService: ServiceSummary;
  assignUserRequest: UserWithClaims | undefined;
  addRequest: (userWithClaims: UserWithClaims) => void;
}

export function AssignUserRow({
  showDropdown,
  user,
  selectedService,
  assignUserRequest,
  addRequest,
}: AssignUserRowProps) {
  const userClaims =
    user.services.find(
      (s) => s.licenseReference === selectedService.licenseReference
    )?.claims ?? [];

  const [open, setOpen] = useState(false);

  const userHasClaimType = (claimType: string) => {
    return hasClaimType(userClaims, claimType);
  };

  const userHasClaimTypeAndValue = (claimType: string, claimValue: string) => {
    return hasClaimTypeAndValue(userClaims, claimType, claimValue);
  };

  const handleClaimSelect = (
    checked: boolean,
    claim: ServiceClaim,
    claimValue: string
  ) => {
    if (assignUserRequest || userClaims.length > 0) {
      let newClaims = assignUserRequest
        ? [...assignUserRequest.claims]
        : [...userClaims];

      const index = newClaims.findIndex((s) => s.type === claim.claimType);

      if (checked) {
        if (index >= 0) {
          newClaims = newClaims.map((c) =>
            c.type === claim.claimType
              ? ({
                  type: c.type,
                  value: [...newClaims[index].value, claimValue],
                } as Claim)
              : c
          );
        } else
          newClaims.push({
            type: claim.claimType,
            value: claimValue !== "" ? [claimValue] : [],
          } as Claim);
      } else {
        if (index === -1) return;
        if (index >= 0) {
          if (newClaims[index].value.length > 1) {
            newClaims = newClaims.map((c) =>
              c.type === claim.claimType
                ? ({
                    type: c.type,
                    value: [
                      ...newClaims[index].value.filter((v) => v !== claimValue),
                    ],
                  } as Claim)
                : c
            );
          } else {
            newClaims.splice(index, 1);
          }
        }
      }

      addRequest({
        user: user,
        claims: newClaims,
      });
    } else {
      addRequest({
        user: user,
        claims: [
          {
            type: claim.claimType,
            value: claimValue !== "" ? [claimValue] : [],
          } as Claim,
        ],
      });
    }
  };

  const isDisplayOneTableInDropdown = () => {
    if (showDropdown) {
      return isContainSameClaimOptionValues(selectedService);
    } else {
      return false;
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Typography>{user.name || "New user"}</Typography>
          <Typography variant="body2" color={"grey"}>
            {user.email}
          </Typography>
        </TableCell>
        {!showDropdown &&
          selectedService.serviceClaims.map((claim, claimIndex) =>
            claim.options.length >= 1 ? (
              claim.options.map((option, optionIndex) => (
                <TableCell key={optionIndex} align="center">
                  <Checkbox
                    defaultChecked={userHasClaimTypeAndValue(
                      claim.claimType,
                      option.claimValue
                    )}
                    onChange={(e, checked) =>
                      handleClaimSelect(checked, claim, option.claimValue)
                    }
                  />
                </TableCell>
              ))
            ) : (
              <TableCell key={claimIndex} align="center">
                <Checkbox
                  defaultChecked={userHasClaimType(claim.claimType)}
                  onChange={(e, checked) =>
                    handleClaimSelect(checked, claim, claim.claimValue)
                  }
                />
              </TableCell>
            )
          )}
        {showDropdown && (
          <TableCell>
            <IconButton onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {isDisplayOneTableInDropdown() && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {getOptionsArray(selectedService).map(
                      (option, optionIndex) => (
                        <TableCell
                          key={optionIndex}
                          align="center"
                          sx={{ fontWeight: "normal" }}
                        >
                          {option.claimValueDescription}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedService.serviceClaims.map((claim, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ fontWeight: "medium" }}>
                        {claim.shortName}
                      </TableCell>
                      {getOptionsArray(selectedService).map(
                        (option, optionIndex) => (
                          <TableCell key={optionIndex} align="center">
                            <Checkbox
                              defaultChecked={userHasClaimTypeAndValue(
                                claim.claimType,
                                option.claimValue
                              )}
                              onChange={(e, checked) =>
                                handleClaimSelect(
                                  checked,
                                  claim,
                                  option.claimValue
                                )
                              }
                            />
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!isDisplayOneTableInDropdown() &&
              selectedService.serviceClaims.map((claim, index) => {
                const sortedOptions = claim.options.sort((item1, item2) =>
                  item1.claimValueDescription > item2.claimValueDescription
                    ? 1
                    : -1
                );
                return (
                  <Box key={index}>
                    <Box sx={{ m: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell></TableCell>
                            {sortedOptions.map((option, optionIndex) => (
                              <TableCell
                                key={optionIndex}
                                align="center"
                                sx={{ fontWeight: "normal" }}
                              >
                                {option.claimValueDescription}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "medium", width: "20%" }}
                            >
                              {claim.shortName}
                            </TableCell>
                            {sortedOptions.map((option, optionIndex) => (
                              <TableCell key={optionIndex} align="center">
                                <Checkbox
                                  defaultChecked={userHasClaimTypeAndValue(
                                    claim.claimType,
                                    option.claimValue
                                  )}
                                  onChange={(e, checked) =>
                                    handleClaimSelect(
                                      checked,
                                      claim,
                                      option.claimValue
                                    )
                                  }
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </Box>
                );
              })}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
